/**
 * Broadpeak Player Adapter Module
 * Bridges FL Player with Broadpeak SmartLib session for SSAI playback.
 * Responsible for ad event handling, player context updates, and session lifecycle coordination.
 *
 * @author Ajithkumar Kasi
 */
import {
  Action,
  AdBreakInfo,
  AdEvent,
  AdEventListener,
  AdInfo,
  AdPlaybackPolicy,
  AdTechnology,
  MediaType,
  PLAYBACK_STATES,
  PlaybackOptions,
  PlaybackStatistics,
  Player,
  PlayerContext,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  TrackInfo,
  TrackType,
  TrackVariantInfo,
  VALUE_UNKNOWN,
} from '@quickplay/player-interface';
import {
  AnyFunc,
  createEventEmitter,
  createLogger,
  createSafeTimeout,
  Error,
  EventEmitter,
  eventObject,
  Logger,
  LoggerLevel,
  TimeoutController,
} from '@quickplay/foundation';
import { AdViewState, BroadpeakAdsPlayer } from '../BroadpeakAdsPlayer';
import { BroadpeakAdsSessionData } from '../BroadpeakAdsSession';
import {
  contentTimeToAdContentTimePosition,
  createAdBreakInfo,
  createAdInfo,
  getAdBreakIndex,
  getValidNumber,
} from './AdUtil';
import { BroadpeakRawAd, BroadpeakRawAdBreak } from '../model/BroadpeakRawAdModels';
import { PALConfiguration } from '@quickplay/pal';

const TAG_NAME = 'FL_BROADPEAK_PLAYER';

export class BroadpeakPlayer implements BroadpeakAdsPlayer {
  private readonly eventEmitter: EventEmitter = createEventEmitter();
  private readonly logger: Logger;

  private internalPlayerContext: PlayerContext = PlayerContext.MAIN;

  private midrollAdBreakIndex = 0;
  private activeAdBreakInfo?: AdBreakInfo;
  private activeAdInfo?: AdInfo;
  private isAdActive = false;
  private readonly adBreakInfoList: AdInfo[] = [];
  private currentAdStartTime?: number;
  private currentAdEndTime?: number;
  private currentAdEndPosition?: number;
  private currentAdBreakEndPosition?: number;
  private readonly broadpeakPlaybackSession: any;
  private readonly FALLBACK_TIMEOUT_IN_MS = 500;
  private readonly timeoutController: TimeoutController;
  private mediaType: MediaType | undefined;

  adEventListener?: AdEventListener;

  constructor(
    private readonly player: Player,
    private readonly internalSession: BroadpeakAdsSessionData,
    _adPlaybackPolicy?: AdPlaybackPolicy,
    logger?: Logger,
  ) {
    this.logger = logger ?? createLogger(LoggerLevel.OFF, TAG_NAME);
    this.timeoutController = createSafeTimeout();

    this.logger.log('Initializing BroadpeakPlayer with session:', internalSession); // Debug log to check session data

    if (this.internalSession.isFallback) {
      this.timeoutController.set(() => {
        this.eventEmitter.publish(AdEvent.ADVERT_ERROR, this.internalSession.fallbackReason);
      }, this.FALLBACK_TIMEOUT_IN_MS);
      return;
    }

    this.broadpeakPlaybackSession = this.internalSession.session;

    if (!this.broadpeakPlaybackSession) {
      this.logger.log('Broadpeak session not initialized');
      return;
    }

    this.init(this.broadpeakPlaybackSession);
  }

  initialize(_initData: PALConfiguration): Promise<string> {
    // Session is created externally and provided through the constructor.
    return Promise.resolve('');
  }

  sendAdClick(interactionType: 'click' | 'invitationAccept' = 'click'): void {
    this.broadpeakPlaybackSession?.adUserInteraction(interactionType);
  }

  sendAdTouch(clickOrTouchEvent: any): void {
    this.broadpeakPlaybackSession?.adTouch(clickOrTouchEvent);
  }

  sendPlaybackStart(): void {
    // No-op. Playback start is handled automatically by the Broadpeak session.
  }

  sendPlaybackEnd(): void {
    // No-op. Playback end is handled automatically by the Broadpeak session.
  }

  shutdown(): void {
    // No-op. Session lifecycle is managed by Broadpeak.
  }

  setAdViewState(state: AdViewState): void {
    this.broadpeakPlaybackSession?.setAdViewState(state);
  }

  // =========================
  // INIT PIPELINE
  // =========================

  private init(session: any): void {
    this.registerAdListeners(session);
    this.subscribePlayerEvents();
  }

  // =========================
  // AD EVENTS
  // =========================

  private registerAdListeners(session: any): void {
    session.setAdEventsListener({
      onAdBreakBegin: this.handleAdBreakBegin,
      onAdBegin: this.handleAdBegin,
      onAdEnd: this.handleAdEnd,
      onAdBreakEnd: this.handleAdBreakEnd,
      onAdSkippable: (
        _adData: any,
        _adBreakData: any,
        _adSkippablePosition: number,
        adEndPosition: number,
        adBreakEndPosition: number,
      ) => {
        this.logger.log(
          '[SDK] onAdSkippable called with adEndPosition:',
          adEndPosition,
          'adBreakEndPosition:',
          adBreakEndPosition,
          'seekableRange:',
          this.player.seekableRange(),
          'currentTime:',
          this.player.currentTime,
          'currentEpochTime:',
          this.player.currentEpochTime,
        );
        this.currentAdEndPosition = adEndPosition;
        this.currentAdBreakEndPosition = adBreakEndPosition;
      },
    });
  }

  private readonly handleAdBreakBegin = (adBreakData: BroadpeakRawAdBreak): void => {
    this.internalPlayerContext = PlayerContext.AD;

    this.eventEmitter.publish('playercontextchanged', this.internalPlayerContext);

    const startPosition = getValidNumber(adBreakData.startPosition);
    const timePosition = contentTimeToAdContentTimePosition(startPosition);

    let adBreakIndex = VALUE_UNKNOWN;

    if (!this.isLive) {
      this.midrollAdBreakIndex = getAdBreakIndex(timePosition, this.midrollAdBreakIndex);

      adBreakIndex = this.midrollAdBreakIndex;
    }

    this.activeAdBreakInfo = createAdBreakInfo(adBreakData, timePosition, adBreakIndex);
    this.eventEmitter.publish(AdEvent.ADVERT_BREAK_START, this.activeAdBreakInfo);
  };

  private readonly handleAdBegin = (adData: BroadpeakRawAd): void => {
    this.activeAdInfo = createAdInfo(adData, this.activeAdBreakInfo);
    this.isAdActive = true;

    const startPosition = getValidNumber(adData.startPosition, VALUE_UNKNOWN);
    const duration = getValidNumber(adData.duration, 0);

    this.currentAdStartTime = startPosition;
    this.currentAdEndTime = startPosition === VALUE_UNKNOWN ? VALUE_UNKNOWN : startPosition + duration;

    this.adBreakInfoList.push(this.activeAdInfo);
    this.eventEmitter.publish(AdEvent.ADVERT_START, this.activeAdInfo);
  };

  private readonly handleAdEnd = (): void => {
    this.eventEmitter.publish(AdEvent.ADVERT_END, this.activeAdInfo);
    this.resetAdState();
  };

  private readonly handleAdBreakEnd = (): void => {
    this.resetAdState();
    this.currentAdBreakEndPosition = undefined;

    this.internalPlayerContext = PlayerContext.MAIN;

    if (this.activeAdBreakInfo) {
      this.activeAdBreakInfo.remainingTimeMs = 0;
    }
    this.eventEmitter.publish('playercontextchanged', this.internalPlayerContext);
    this.eventEmitter.publish(AdEvent.ADVERT_BREAK_END, this.activeAdBreakInfo);
    this.activeAdBreakInfo = undefined;
  };

  private readonly resetAdState = (): void => {
    this.isAdActive = false;
    this.currentAdStartTime = undefined;
    this.currentAdEndTime = undefined;
    this.currentAdEndPosition = undefined;
    this.activeAdInfo = undefined;
  };

  // =========================
  // SUBSCRIPTIONS
  // =========================

  private subscribePlayerEvents(): void {
    this.player.subscribe('playbackstatechanged', this.onPlaybackStateChanged);
    this.player.subscribe('progressupdate', this.onProgressUpdate);
    this.player.subscribe('contentloaded', this.onContentLoaded);
    this.player.subscribe('error', this.handleError);
  }

  private unsubscribePlayerEvents(): void {
    this.player.unsubscribe('playbackstatechanged', this.onPlaybackStateChanged);
    this.player.unsubscribe('progressupdate', this.onProgressUpdate);
    this.player.unsubscribe('contentloaded', this.onContentLoaded);
    this.player.unsubscribe('error', this.handleError);
  }

  // =========================
  // PLAYER EVENTS
  // =========================

  private readonly onPlaybackStateChanged = (playbackState: PLAYBACK_STATES): void => {
    if (playbackState === PLAYBACK_STATES.STOPPED) {
      this.logger.log('onPlayerEvent -> STOP');
      this.releaseSession();
    }
  };

  private readonly onProgressUpdate = (): void => {
    if (!this.isAdActive || !this.activeAdInfo) {
      return;
    }

    /**
     * Broadpeak ad start/end timestamps use different timebases depending on
     * the media type and stream type.
     *
     * For live non-DASH streams (e.g. HLS), use `currentEpochTime` and convert
     * it to milliseconds so that the playback position matches Broadpeak's
     * epoch-based ad timestamps.
     *
     * For DASH streams, and for VOD streams, use Shaka Player's `currentTime`.
     */
    const currentTimeMs =
      this.isLive && this.mediaType !== MediaType.DASH
        ? this.player.currentEpochTime * 1000
        : Math.round(this.player.currentTime * 1000);

    const startMs = this.currentAdStartTime ?? 0;
    const endMs = this.currentAdEndTime;

    // Ad already ended
    if (endMs != null && endMs > 0 && currentTimeMs > endMs) {
      return;
    }

    const progressMs = startMs > 0 ? currentTimeMs - startMs : currentTimeMs;

    const durationMs = this.activeAdInfo.durationMs ?? 0;

    const remainingTimeMs = endMs != null && endMs > 0 ? Math.max(0, durationMs - progressMs) : VALUE_UNKNOWN;

    this.activeAdInfo.remainingTimeMs = remainingTimeMs;

    this.eventEmitter.publish(AdEvent.ADVERT_PROGRESS_UPDATE, this.activeAdInfo, progressMs / 1000);
  };

  private readonly onContentLoaded = (): void => {
    this.setMediaType();
    // Broadpeak delivers ad metadata reactively via setAdEventsListener callbacks,
    // so we publish whatever ad info has been collected so far.
    this.eventEmitter.publish('adbreaks', [...this.adBreakInfoList]);
  };

  private readonly handleError = (_error: any): void => {
    if (this.activeAdBreakInfo) {
      this.eventEmitter.publish('aderror', _error);
    }
    this.releaseSession();
  };

  // =========================
  // LIFECYCLE
  // =========================

  private readonly releaseSession = (): void => {
    this.timeoutController.clear();
    this.unsubscribePlayerEvents();
    this.internalSession.destroy();

    this.resetAdState();
    this.midrollAdBreakIndex = 0;
    this.adBreakInfoList.length = 0;
  };

  private setMediaType(): void {
    const url = this.broadpeakSession.broadpeakAdUrl?.toLowerCase();

    if (url?.includes('.mpd')) {
      this.mediaType = MediaType.DASH;
    } else if (url?.includes('.m3u8')) {
      this.mediaType = MediaType.HLS;
    } else {
      this.mediaType = undefined;
    }
  }

  // =========================
  // PUBLIC API (PASS THROUGH)
  // =========================

  get adSessionId(): string | undefined {
    return this.broadpeakPlaybackSession?.id;
  }
  get adInsertionType(): AdTechnology {
    return AdTechnology.SSAI;
  }

  get broadpeakSession(): BroadpeakAdsSessionData {
    return this.internalSession;
  }

  get volume(): number {
    return this.player.volume ?? 0;
  }

  set volume(volumeLevel: number) {
    this.player.volume = volumeLevel;
  }

  get availableBitrates(): number[] {
    return this.player.availableBitrates ?? [];
  }

  get currentEpochTime(): number {
    return this.player.currentEpochTime ?? 0;
  }

  get currentTime(): number {
    // To do: calculate current time by excluding ads duration in VOD
    return this.player.currentTime;
  }

  get duration(): number {
    // To do: calculate duration by excluding ads duration in VOD
    return this.player.duration;
  }

  get internalPlayerName(): string {
    return this.player.internalPlayerName;
  }

  get internalPlayerVersion(): string {
    return this.player.internalPlayerVersion;
  }

  get isBuffering(): boolean {
    return this.player.isBuffering ?? false;
  }

  get isLive(): boolean {
    return this.player.isLive ?? false;
  }

  get isMute(): boolean {
    return this.player.isMute ?? false;
  }

  get isSeeking(): boolean {
    return this.player.isSeeking ?? false;
  }

  get maxBitrate(): number | undefined {
    return this.player.maxBitrate;
  }

  set maxBitrate(value: number | undefined) {
    this.player.maxBitrate = value;
  }

  get minBitrate(): number | undefined {
    return this.player.minBitrate;
  }

  set minBitrate(value: number | undefined) {
    this.player.minBitrate = value;
  }

  get playbackOptions(): PlaybackOptions | undefined {
    return this.player.playbackOptions;
  }

  get playbackRate(): number {
    return this.player.playbackRate;
  }

  set playbackRate(rate: number) {
    this.player.playbackRate = rate;
  }

  get playbackStatistics(): PlaybackStatistics | undefined {
    return this.player.playbackStatistics;
  }

  get playerContext(): PlayerContext {
    return this.internalPlayerContext;
  }

  get playerState(): string {
    return this.player.playerState;
  }

  get rawLibrary(): any {
    return this.player.rawLibrary;
  }

  get rawPlayer(): any {
    return this.player.rawPlayer;
  }

  get remainingTime(): number {
    return 0;
  }

  abort(error: Error): void {
    this.releaseSession();
    this.player.abort(error);
  }

  accessFunctionHandler(json: string): void {
    this.player.accessFunctionHandler(json);
  }

  currentOffsetFromLiveEdge(): number {
    return this.player.currentOffsetFromLiveEdge();
  }

  discardAdBreak(): void {
    if (this.currentAdBreakEndPosition != null) {
      this.player.seek(this.currentAdBreakEndPosition / 1000);
    }
  }

  getActiveTracks(): TrackInfo[] {
    return this.player.getActiveTracks();
  }

  getAllTracks(): TrackInfo[] {
    return this.player.getAllTracks();
  }

  getVariantTracks(): TrackVariantInfo[] {
    return this.player.getVariantTracks() ?? [];
  }

  getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
    return this.player.getAvailableTrackVariants(trackType);
  }

  getCuePoints(): number[] {
    return [];
  }

  getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
    return Promise.resolve(this.player.getThumbnail(position));
  }

  load(): void {
    this.player.load();
  }

  muteOrUnmute(): void {
    this.player.muteOrUnmute?.();
  }

  pause(): void {
    this.player.pause();
  }

  play(): void {
    this.player.play();
  }

  restrictQuality(enableRestriction: boolean): void {
    this.player.restrictQuality(enableRestriction);
  }

  seek(seekPoint: number): void {
    this.player.seek(seekPoint);
  }

  seekToLiveEdge(): void {
    this.player.seekToLiveEdge();
  }

  seekableRange(): SeekRangeInfo {
    if (this.player.isLive) {
      return this.player.seekableRange();
    }
    return { start: 0, end: this.duration };
  }

  selectTrack(track?: TrackInfo): void {
    this.player.selectTrack(track);
  }

  send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
    this.player.send(eventName, action, metadata);
  }

  setPlaybackOptions(playbackOptions: PlaybackOptions): void {
    this.player.setPlaybackOptions(playbackOptions);
  }

  setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
    this.player.setPreferredMimeTypes(trackType, mimeTypes);
  }

  setRequestInterceptor(requestInterceptor: (requestType: RequestType, request: any) => void): void {
    this.player.setRequestInterceptor(requestInterceptor);
  }

  setResponseInterceptor(responseInterceptor: (requestType: RequestType, response: any) => void): void {
    this.player.setResponseInterceptor(responseInterceptor);
  }

  availableMimeTypes(trackType: TrackType): Array<string> {
    return this.player.availableMimeTypes(trackType);
  }

  skipAd(): void {
    if (this.currentAdEndPosition != null) {
      this.player.seek(this.currentAdEndPosition / 1000);
    }
  }

  stop(): Promise<void> {
    this.releaseSession();
    return this.player.stop();
  }

  unloadAndSavePreload(): Promise<void> {
    return Promise.resolve();
  }

  restoreFromPreload(): void {
    // no-op
  }

  subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.eventEmitter.subscribe(eventName, callback);
    this.player.subscribe(eventName, callback);
  }

  unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.eventEmitter.unsubscribe(eventName, callback);
    this.player.unsubscribe(eventName, callback);
  }

  resize(_width: number, _height: number, _isFullscreen: boolean): void {
    // no-op
  }
}
